import _ from 'lodash'
import PropTypes from 'prop-types'
import * as React from 'react'

import useTableQuestionRow from '../../../hooks/useTableQuestionRow'
import { hasText } from '../../../utils/helpers'
import ZentakeFormContext from '../ZentakeFormContext'
import { TableOpenEndedRender } from './TableOpenEndedRender'

function TableOpenEndedQuestionFC({
  question,
  isOfficeUse,
  response,
  sessions,
  mode,
}) {
  const { getTableQuestionExtraRows, ...formContext } =
    React.useContext(ZentakeFormContext)
  const readonly = formContext.isQuestionReadonly(question)
  const willShowNotAllowedCursor =
    formContext.willShowNotAllowedCursor(question)

  const isDirty = formContext.isQuestionDirty(question)
  const isValid = formContext.isQuestionValid(question)
  const questionError = formContext.getQuestionError(question)

  const [otherRows, setOtherRows] = React.useState([])

  const [hasError, setHasError] = React.useState(false)
  const [errorMessage, setErrorMessage] = React.useState(
    'This field is required'
  )

  const officeUsePreview = mode === 'preview' && question.is_office_use_only

  React.useEffect(() => {
    const extraRowsFromAnswers = getTableQuestionExtraRows(question)
    setOtherRows(extraRowsFromAnswers)
    // eslint-disable-next-line
  }, [question])

  const [tableQuestionRows, addTableRow, removeTableRow] = useTableQuestionRow(
    question?.question_rows ?? [],
    otherRows ?? []
  )

  React.useEffect(() => {
    setHasError(
      (isDirty && question?.is_required && !isValid) || hasText(questionError)
    )
    setErrorMessage(
      questionError && typeof questionError === 'string'
        ? questionError
        : 'This field is required'
    )
  }, [isDirty, question.is_required, isValid, questionError])

  function getCellValue(rowId, columnId) {
    const answer = formContext.getAnswerCell(rowId, columnId)
    return answer?.text_value ?? ''
  }

  const handleCellChange = (
    rowData,
    columnData,
    value,
    local = false,
    removeRow = false
  ) => {
    formContext?.onAnswerCellChanged?.(question, rowData, columnData, {
      value,
      local,
      removeRow,
    })
  }

  const onRemoveTableRow = (rowId, isLocal = false) => {
    removeTableRow(rowId, isLocal)
    handleCellChange(rowId, null, null, isLocal, true)
  }

  return (
    <TableOpenEndedRender
      errorMessage={errorMessage}
      getCellValue={getCellValue}
      handleCellChange={handleCellChange}
      hasError={hasError}
      help_text={question.help_text}
      isDirty={isDirty}
      isOfficeUse={isOfficeUse || officeUsePreview}
      isRequired={question.is_required}
      isValid={isValid}
      label={question.text}
      onAddTableRow={addTableRow}
      onRemoveTableRow={onRemoveTableRow}
      questionError={questionError}
      questionId={question.id}
      question_columns={question.question_columns}
      question_rows={tableQuestionRows}
      readonly={readonly}
      response={response}
      sessions={sessions}
      willShowNotAllowedCursor={willShowNotAllowedCursor}
    />
  )
}

TableOpenEndedQuestionFC.propTypes = {
  question: PropTypes.object.isRequired,
}

function areEqual(p, n) {
  return _.isEqual(p.question, n.question)
}

const TableOpenEndedQuestion = React.memo(TableOpenEndedQuestionFC, areEqual)

export default TableOpenEndedQuestion
